import React, { useRef, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { observer } from "mobx-react"
import { Button, Flex, Form, Input, Typography } from "antd"
import { useForm } from "antd/es/form/Form"

import crystal from "@src/app/assets/crystal-white.png"
import crystalRetina from "@src/app/assets/crystal-white@2x.png"
import { FormContainer } from "@src/app/components/FormContainer"
import { Logo } from "@src/app/components/Logo"
import { AuthLayout } from "@src/app/layouts/AuthLayout"
import UiStore from "@src/app/uiStore"
import { ReCaptchaProvider } from "@src/pages/auth"
import css from "./index.module.css"
import { RecoveryStore } from "./store"

const { Item } = Form

const { Paragraph, Title } = Typography

const PasswordRecoveryPageContent = observer(() => {
  const [store] = useState(new RecoveryStore())
  const { isTransferioTheme } = UiStore()
  const { recover } = store
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [form] = useForm()
  const [captchaError, setCaptchaError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitting = useRef(false)

  const onSubmit = async () => {
    if (submitting.current) return

    setCaptchaError(undefined)

    if (!executeRecaptcha) {
      setCaptchaError(
        "Verification is still loading. Please try again shortly.",
      )

      return
    }

    submitting.current = true
    setIsSubmitting(true)

    try {
      let token: string

      try {
        token = await executeRecaptcha("recover")
        if (!token) throw new Error("Empty reCAPTCHA token")
      } catch {
        setCaptchaError(
          "Verification failed. Please try again or reload the page.",
        )

        return
      }

      await recover(form.getFieldsValue(), token)
    } finally {
      submitting.current = false
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <AuthLayout>
        <div>
          {!isTransferioTheme && <Logo size="full" />}
          <Typography className={css.typography}>
            <Title className={css.title}>Password recovery</Title>
            <Paragraph className={css.subtitle}>
              Please enter your email address. You will receive a recovery code
            </Paragraph>
          </Typography>
          <FormContainer
            baseError={captchaError}
            className={css.container}
            form={form}
            name="recovery"
            onFinish={onSubmit}
          >
            <Flex gap={16} vertical>
              <Item
                label="Email"
                name="email"
                rules={[
                  {
                    message: "Must be a valid email address",
                    type: "email",
                  },
                  {
                    message: "Please input your Email",
                    required: true,
                  },
                ]}
              >
                <Input />
              </Item>

              <Item>
                <Button
                  className={css.button}
                  disabled={!executeRecaptcha}
                  htmlType="submit"
                  id="GENERATED_ID_PASSWORDRECOVERY_BUTTON_RECEIVE_A_RECOVERY_1"
                  loading={isSubmitting}
                  type="primary"
                >
                  Receive a recovery code
                </Button>
              </Item>
            </Flex>
          </FormContainer>
        </div>
      </AuthLayout>
    </>
  )
})

export const PasswordRecoveryPage = () => (
  <ReCaptchaProvider>
    <PasswordRecoveryPageContent />
  </ReCaptchaProvider>
)
